// SVG Connector Generator Service
// Centralized service for generating consistent connector diagrams across all components

export const SVG_CONSTANTS = {
  // Visual constants
  CONNECTOR_WIDTH: 60,
  CONNECTOR_HEIGHT: 60,
  PX_PER_INCH: 8,
  
  // Cable thickness based on type and gauge
  CABLE_THICKNESS: {
    audio: { default: 10, thin: 8, thick: 12 },
    power: { '16': 14, '14': 16, '12': 18, '10': 20, '8': 22, default: 16 },
    video: { default: 12 },
    data: { default: 10 },
    dmx: { default: 10 }
  },
  
  // Enhanced color palette with modern gradients
  COLORS: {
    // Primary cable colors
    AUDIO1: '#3b82f6',             // Modern blue
    AUDIO2: '#ef4444',             // Modern red
    POWER: '#f97316',              // Modern orange
    VIDEO: '#8b5cf6',              // Modern purple
    DATA: '#10b981',               // Modern green
    DMX: '#eab308',                // Modern yellow
    
    // Background and UI colors
    BACKGROUND: '#ffffff',
    BACKGROUND_ALT: '#f8fafc',
    SURFACE: '#ffffff',
    SURFACE_HOVER: '#f1f5f9',
    
    // Text colors
    TEXT_PRIMARY: '#1e293b',
    TEXT_SECONDARY: '#64748b',
    TEXT_MUTED: '#94a3b8',
    
    // Border and stroke colors
    BORDER: '#e2e8f0',
    BORDER_HOVER: '#cbd5e1',
    STROKE: '#475569',
    
    // Shadow and depth colors
    SHADOW_LIGHT: 'rgba(0, 0, 0, 0.05)',
    SHADOW_MEDIUM: 'rgba(0, 0, 0, 0.1)',
    SHADOW_HEAVY: 'rgba(0, 0, 0, 0.15)',
    
    // Accent colors
    ACCENT_PRIMARY: '#3b82f6',
    ACCENT_SECONDARY: '#10b981',
    ACCENT_WARNING: '#f59e0b',
    ACCENT_ERROR: '#ef4444',
    
    // Legacy support
    CENTRAL_BAR: '#475569',
    WHITE: '#ffffff',
    DIMENSION_LINE: '#94a3b8',
    DIMENSION_TEXT: '#475569',
    CONNECTOR_STROKE: '#475569',
    HEAT_SHRINK: '#ef4444',
    TECH_FLEX: '#64748b'
  },
  
  // Enhanced stroke widths
  STROKE_WIDTHS: {
    AUDIO: 10,
    POWER: 14,
    CENTRAL_BAR: 4,
    DIMENSION_LINE: 2,
    CONNECTOR_STROKE: 2,
    CABLE: 12,
    CABLE_THIN: 8,
    CABLE_THICK: 16
  }
};

// Generate enhanced shadow filters with multiple layers
export const generateShadowFilter = () => `
  <defs>
    <!-- Enhanced shadow system -->
    <filter id="shadow-soft" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="rgba(0,0,0,0.08)" />
      <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="rgba(0,0,0,0.04)" />
    </filter>
    
    <filter id="shadow-medium" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="rgba(0,0,0,0.12)" />
      <feDropShadow dx="0" dy="6" stdDeviation="12" floodColor="rgba(0,0,0,0.06)" />
    </filter>
    
    <filter id="shadow-heavy" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="rgba(0,0,0,0.15)" />
      <feDropShadow dx="0" dy="8" stdDeviation="16" floodColor="rgba(0,0,0,0.08)" />
    </filter>
    
    <!-- Cable-specific shadows -->
    <filter id="cable-shadow" x="-30%" y="-30%" width="160%" height="160%">
      <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.2)" />
      <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="rgba(0,0,0,0.1)" />
    </filter>
    
    <!-- Connector-specific shadows -->
    <filter id="connector-shadow" x="-30%" y="-30%" width="160%" height="160%">
      <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="rgba(0,0,0,0.25)" />
      <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="rgba(0,0,0,0.12)" />
    </filter>
    
    <!-- Legacy shadow support -->
    <filter id="shadow" x="-30%" y="-30%" width="160%" height="160%">
      <feDropShadow dx="3" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.25" />
    </filter>
    <filter id="cableShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="2" dy="3" stdDeviation="2" floodColor="#000" floodOpacity="0.2" />
    </filter>
    <filter id="connectorShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
    </filter>
    
    <!-- Modern gradients -->
    <linearGradient id="cable-gradient-audio" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
      <stop offset="50%" style="stop-color:#60a5fa;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#3b82f6;stop-opacity:1" />
    </linearGradient>
    
    <linearGradient id="cable-gradient-power" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#f97316;stop-opacity:1" />
      <stop offset="50%" style="stop-color:#fb923c;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#f97316;stop-opacity:1" />
    </linearGradient>
    
    <linearGradient id="cable-gradient-video" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#8b5cf6;stop-opacity:1" />
      <stop offset="50%" style="stop-color:#a78bfa;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#8b5cf6;stop-opacity:1" />
    </linearGradient>
    
    <linearGradient id="cable-gradient-data" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#10b981;stop-opacity:1" />
      <stop offset="50%" style="stop-color:#34d399;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#10b981;stop-opacity:1" />
    </linearGradient>
    
    <linearGradient id="cable-gradient-dmx" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#eab308;stop-opacity:1" />
      <stop offset="50%" style="stop-color:#fbbf24;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#eab308;stop-opacity:1" />
    </linearGradient>
    
    <!-- Connector gradients -->
    <linearGradient id="connector-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#f8fafc;stop-opacity:1" />
    </linearGradient>
    
    <linearGradient id="connector-gradient-hover" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#f1f5f9;stop-opacity:1" />
    </linearGradient>
    
    <!-- Background gradients -->
    <linearGradient id="background-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#f8fafc;stop-opacity:1" />
    </linearGradient>
  </defs>
`;

// Get cable thickness based on type and gauge
export const getCableThickness = (cableType, wireGauge) => {
  const thicknessMap = SVG_CONSTANTS.CABLE_THICKNESS;
  
  if (cableType === 'power' && wireGauge) {
    return thicknessMap.power[wireGauge] || thicknessMap.power.default;
  }
  
  return thicknessMap[cableType]?.default || thicknessMap.audio.default;
};

// Get cable color based on type
export const getCableColor = (cableType, wireGauge) => {
  const colors = SVG_CONSTANTS.COLORS;
  
  switch (cableType) {
    case 'power': return colors.POWER;
    case 'audio': return colors.AUDIO1;
    case 'video': return colors.VIDEO;
    case 'data': return colors.DATA;
    case 'dmx': return colors.DMX;
    default: return colors.AUDIO1;
  }
};

// Get cable gradient based on type
export const getCableGradient = (cableType) => {
  switch (cableType) {
    case 'power': return 'url(#cable-gradient-power)';
    case 'audio': return 'url(#cable-gradient-audio)';
    case 'video': return 'url(#cable-gradient-video)';
    case 'data': return 'url(#cable-gradient-data)';
    case 'dmx': return 'url(#cable-gradient-dmx)';
    default: return 'url(#cable-gradient-audio)';
  }
};

// Generate enhanced dimension line with modern styling
export const generateDimensionLine = (startX, endX, y, label, unit) => {
  const midX = (startX + endX) / 2;
  
  // Handle different label formats and units
  let displayLabel;
  if (unit === 'ft') {
    // If label contains '=', extract the value after it
    if (label.includes('=')) {
      const value = parseFloat(label.split('=')[1]);
      displayLabel = `${value}'`;
    } else {
      // If label is just a number, use it directly
      const value = parseFloat(label);
      displayLabel = `${value}'`;
    }
  } else {
    // For inches, just add the inch symbol
    displayLabel = `${label}"`;
  }
    
  return `
    <!-- Dimension line -->
    <line x1="${startX}" y1="${y}" x2="${endX}" y2="${y}" stroke="${SVG_CONSTANTS.COLORS.DIMENSION_LINE}" strokeWidth="${SVG_CONSTANTS.STROKE_WIDTHS.DIMENSION_LINE}" strokeDasharray="4,4" />
    
    <!-- End caps -->
    <line x1="${startX}" y1="${y-6}" x2="${startX}" y2="${y+6}" stroke="${SVG_CONSTANTS.COLORS.DIMENSION_LINE}" strokeWidth="2" />
    <line x1="${endX}" y1="${y-6}" x2="${endX}" y2="${y+6}" stroke="${SVG_CONSTANTS.COLORS.DIMENSION_LINE}" strokeWidth="2" />
    
    <!-- Label background -->
    <rect x="${midX - 50}" y="${y - 25}" width="100" height="30" rx="15" fill="${SVG_CONSTANTS.COLORS.SURFACE}" stroke="${SVG_CONSTANTS.COLORS.BORDER}" strokeWidth="1" filter="url(#shadow-soft)" />
    
    <!-- Label text -->
    <text x="${midX}" y="${y - 8}" textAnchor="middle" fontSize="16" fill="${SVG_CONSTANTS.COLORS.TEXT_PRIMARY}" fontWeight="600" dominantBaseline="middle">
      ${displayLabel}
    </text>
  `;
};

// Generate modern connector with enhanced styling
export const generateConnectorPoint = (connector, position, pointId, cableType, onClick) => {
  const { x, y } = position;
  const { CONNECTOR_WIDTH: w, CONNECTOR_HEIGHT: h } = SVG_CONSTANTS;
  
  // Determine connector style based on type and configuration
  const getConnectorStyle = () => {
    if (!connector) return { 
      fill: SVG_CONSTANTS.COLORS.SURFACE, 
      stroke: SVG_CONSTANTS.COLORS.BORDER,
      accent: SVG_CONSTANTS.COLORS.ACCENT_PRIMARY 
    };
    
    // XLR connectors
    if (connector.id?.includes('NC3') || connector.label?.toLowerCase().includes('xlr')) {
      return { 
        fill: SVG_CONSTANTS.COLORS.SURFACE, 
        stroke: '#1e293b',
        accent: '#1e293b' 
      };
    }
    
    // Speakon connectors
    if (connector.id?.includes('NL') || connector.label?.toLowerCase().includes('speakon')) {
      return { 
        fill: SVG_CONSTANTS.COLORS.SURFACE, 
        stroke: '#1d4ed8',
        accent: '#1d4ed8' 
      };
    }
    
    // Power connectors (IEC, etc.)
    if (connector.id?.includes('IEC') || connector.label?.toLowerCase().includes('iec')) {
      return { 
        fill: SVG_CONSTANTS.COLORS.SURFACE, 
        stroke: '#ea580c',
        accent: '#ea580c' 
      };
    }
    
    // RCA connectors
    if (connector.id?.includes('RCA') || connector.label?.toLowerCase().includes('rca')) {
      return { 
        fill: SVG_CONSTANTS.COLORS.SURFACE, 
        stroke: '#dc2626',
        accent: '#dc2626' 
      };
    }
    
    // Default
    return { 
      fill: SVG_CONSTANTS.COLORS.SURFACE, 
      stroke: SVG_CONSTANTS.COLORS.STROKE,
      accent: SVG_CONSTANTS.COLORS.ACCENT_PRIMARY 
    };
  };
  
  const style = getConnectorStyle();
  
  return `
    <g onClick="${onClick}" style="cursor: pointer;" class="connector-group">
      <!-- Connector background with gradient -->
      <rect 
        x="${x - w/2}" 
        y="${y - h/2}" 
        width="${w}" 
        height="${h}" 
        rx="12" 
        fill="url(#connector-gradient)" 
        stroke="${style.stroke}" 
        strokeWidth="2" 
        filter="url(#connector-shadow)"
        style="transition: all 0.2s ease-in-out;"
      />
      
      <!-- Connector image or placeholder -->
      ${connector?.image ? `
        <image 
          x="${x - w/2 + 8}" 
          y="${y - h/2 + 8}" 
          width="${w - 16}" 
          height="${h - 16}" 
          href="${connector.image}"
          preserveAspectRatio="xMidYMid meet"
          style="border-radius: 8px;"
        />
      ` : `
        <!-- Modern placeholder with icon -->
        <g>
          <circle cx="${x}" cy="${y - 5}" r="8" fill="${style.accent}" opacity="0.8" />
          <rect x="${x - 12}" y="${y + 5}" width="24" height="3" rx="1.5" fill="${style.accent}" opacity="0.6" />
          <rect x="${x - 8}" y="${y + 10}" width="16" height="2" rx="1" fill="${style.accent}" opacity="0.4" />
        </g>
      `}
      
      <!-- Gender indicator -->
      ${connector?.gender ? `
        <circle cx="${x}" cy="${y - h/2 - 8}" r="6" fill="${style.accent}" />
        <text 
          x="${x}" 
          y="${y - h/2 - 8}" 
          textAnchor="middle" 
          fontSize="10" 
          fill="white" 
          fontWeight="700"
          dominantBaseline="middle"
        >
          ${connector.gender.toUpperCase()}
        </text>
      ` : ''}
      
      <!-- Connector label -->
      <text 
        x="${x}" 
        y="${y + h/2 + 20}" 
        textAnchor="middle" 
        fontSize="14" 
        fill="${SVG_CONSTANTS.COLORS.TEXT_PRIMARY}" 
        fontWeight="600"
      >
        ${pointId}
      </text>
      
      <!-- Configuration indicator -->
      ${connector?.configuration && connector.configuration !== 'standard' ? `
        <rect 
          x="${x - 15}" 
          y="${y + h/2 + 25}" 
          width="30" 
          height="16" 
          rx="8" 
          fill="${SVG_CONSTANTS.COLORS.ACCENT_SECONDARY}" 
          opacity="0.9"
        />
        <text 
          x="${x}" 
          y="${y + h/2 + 35}" 
          textAnchor="middle" 
          fontSize="9" 
          fill="white" 
          fontWeight="700"
          dominantBaseline="middle"
        >
          ${connector.configuration === 'panel-mount' ? 'PNL' : 
            connector.configuration === 'right-angle' ? 'RA' : 
            connector.configuration.toUpperCase()}
        </text>
      ` : ''}
    </g>
  `;
};

// Generate enhanced central bar with modern styling
export const generateCentralBar = (x, y, width, height = 30) => `
  <rect 
    x="${x}" 
    y="${y}" 
    width="${width}" 
    height="${height}" 
    rx="15" 
    fill="url(#connector-gradient)" 
    stroke="${SVG_CONSTANTS.COLORS.BORDER}" 
    strokeWidth="1" 
    filter="url(#shadow-medium)" 
  />
`;

// Generate enhanced wire branch with modern styling
export const generateWireBranch = (pathData, wireType, cableType, wireGauge, strokeWidth = null) => {
  const gradient = getCableGradient(cableType);
  const thickness = strokeWidth || getCableThickness(cableType, wireGauge);
                
  return `
    <!-- Wire branch with gradient -->
    <path 
      d="${pathData}" 
      stroke="${gradient}" 
      strokeWidth="${thickness}" 
      fill="none" 
      filter="url(#cable-shadow)" 
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    
    <!-- Wire highlight -->
    <path 
      d="${pathData}" 
      stroke="white" 
      strokeWidth="${thickness - 4}" 
      fill="none" 
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.3"
    />
  `;
};

// Generate enhanced finishing elements
export const generateFinishingElements = (config, pathData, cableType, wireGauge) => {
  const finishing = config.finishing || {};
  const elements = [];
  
  // Heat shrink
  if (finishing.heatShrink && finishing.heatShrink !== 'none') {
    const heatShrinkColor = finishing.heatShrink.type === 'custom' && finishing.heatShrink.text ? 
      `#${finishing.heatShrink.text}` : SVG_CONSTANTS.COLORS.ACCENT_ERROR;
    
    elements.push(`
      <path 
        d="${pathData}" 
        stroke="${heatShrinkColor}" 
        strokeWidth="${getCableThickness(cableType, wireGauge) + 6}" 
        fill="none" 
        strokeDasharray="6,6"
        opacity="0.8"
        strokeLinecap="round"
      />
    `);
  }
  
  // Tech flex
  if (finishing.techFlex && finishing.techFlex !== 'none') {
    elements.push(`
      <path 
        d="${pathData}" 
        stroke="${SVG_CONSTANTS.COLORS.TECH_FLEX}" 
        strokeWidth="${getCableThickness(cableType, wireGauge) + 4}" 
        fill="none" 
        strokeDasharray="3,3"
        opacity="0.9"
        strokeLinecap="round"
      />
    `);
  }
  
  return elements.join('');
};

// Calculate responsive scaling for SVG content
export const calculateScaling = (contentBounds, svgWidth, svgHeight, padding = 60) => {
  const { minX, maxX, minY, maxY } = contentBounds;
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  
  let scale = 1;
  const availableWidth = svgWidth - 2 * padding;
  const availableHeight = svgHeight - 2 * padding;
  
  if (contentWidth > availableWidth) {
    scale = Math.min(scale, availableWidth / contentWidth);
  }
  if (contentHeight > availableHeight) {
    scale = Math.min(scale, availableHeight / contentHeight);
  }
  
  scale = Math.max(scale, 0.6);
  
  const groupX = (svgWidth - contentWidth * scale) / 2 - minX * scale + padding;
  const groupY = (svgHeight - contentHeight * scale) / 2 - minY * scale + padding;
  
  return {
    scale,
    transformX: groupX,
    transformY: groupY,
    transform: `translate(${groupX}px, ${groupY}px) scale(${scale})`,
    style: {
      transition: 'transform 0.5s cubic-bezier(0.4,0,0.2,1)',
      transform: `translate(${groupX}px, ${groupY}px) scale(${scale})`,
      transformOrigin: '0 0'
    }
  };
};

// Generate modern configuration toggle buttons
export const generateConfigurationButtons = (currentConfig, pointId, onConfigChange, isDisabled = () => false) => `
  <ToggleButtonGroup
    value="${currentConfig}"
    exclusive
    onClick={(e) => e.stopPropagation()}
    onChange={(e, newConfig) => {
      e.stopPropagation();
      if (newConfig !== null) {
        ${onConfigChange}('${pointId}', newConfig);
      }
    }}
    aria-label="configuration for connector ${pointId}"
    size="small"
    sx={{ 
      width: '100%',
      '& .MuiToggleButton-root': {
        fontSize: '0.7rem',
        px: 0.5,
        py: 0.3,
        minWidth: 'unset',
        flex: 1,
        border: '1px solid',
        borderColor: '${SVG_CONSTANTS.COLORS.BORDER}',
        backgroundColor: '${SVG_CONSTANTS.COLORS.SURFACE}',
        color: '${SVG_CONSTANTS.COLORS.TEXT_SECONDARY}',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: '${SVG_CONSTANTS.COLORS.SURFACE_HOVER}',
          borderColor: '${SVG_CONSTANTS.COLORS.BORDER_HOVER}',
        },
        '&.Mui-selected': {
          backgroundColor: '${SVG_CONSTANTS.COLORS.ACCENT_PRIMARY}',
          color: 'white',
          border: '1px solid ${SVG_CONSTANTS.COLORS.ACCENT_PRIMARY}',
          '&:hover': {
            backgroundColor: '${SVG_CONSTANTS.COLORS.ACCENT_PRIMARY}',
            opacity: 0.9
          }
        }
      }
    }}
  >
    <ToggleButton 
      value="standard" 
      aria-label="standard" 
      sx={{ flex: 1, minWidth: 0, px: 0.5 }}
      disabled={${isDisabled}('${pointId}', 'standard')}
    >
      STD
    </ToggleButton>
    <ToggleButton 
      value="right-angle" 
      aria-label="right angle" 
      sx={{ flex: 1, minWidth: 0, px: 0.5 }}
      disabled={${isDisabled}('${pointId}', 'right-angle')}
    >
      RA
    </ToggleButton>
    <ToggleButton 
      value="panel-mount" 
      aria-label="panel mount" 
      sx={{ flex: 1, minWidth: 0, px: 0.5 }}
      disabled={${isDisabled}('${pointId}', 'panel-mount')}
    >
      PNL
    </ToggleButton>
  </ToggleButtonGroup>
`;

// Utility function to check if right-angle is disabled for Speakon
export const isRightAngleDisabled = (connector, configuration) => {
  if (configuration !== 'right-angle') return false;
  if (!connector) return false;
  
  return connector.family === 'Speakon' || 
         (connector.label?.toLowerCase().includes('speakon') ?? false) || 
         (connector.description?.toLowerCase().includes('speakon') ?? false);
};

// Layout templates for different connector configurations
export const LAYOUT_TEMPLATES = {
  ZIP_AUDIO_AUDIO: 'zip-audio-audio',
  HYBRID: 'hybrid', 
  WYE: 'wye',
  HYBRID_POWER_AUDIO_AUDIO: 'hybrid-power-audio-audio'
};

const svgConnectorGenerator = {
  SVG_CONSTANTS,
  generateShadowFilter,
  generateDimensionLine,
  generateConnectorPoint,
  generateCentralBar,
  generateWireBranch,
  generateFinishingElements,
  getCableThickness,
  getCableColor,
  getCableGradient,
  calculateScaling,
  generateConfigurationButtons,
  isRightAngleDisabled,
  LAYOUT_TEMPLATES
};

export default svgConnectorGenerator; 